import * as React from "react";
import { CheckCircle2, AlertCircle, Loader2, Send } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useTestSendTemplate } from "@/lib/emailTemplates/api";

interface TestSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateKey: string;
  subject: string;
  html_body: string;
  text_body: string;
  /** SA's own email — anti-spam: can't be changed in the UI. */
  callerEmail: string;
}

/**
 * "Send a test to me" confirmation dialog.
 *
 * Anti-spam by design: the recipient is the calling SA's email, not an
 * arbitrary input. The BE enforces this regardless of what the FE sends.
 */
export function TestSendDialog({
  open,
  onOpenChange,
  templateKey,
  subject,
  html_body,
  text_body,
  callerEmail,
}: TestSendDialogProps) {
  const mutation = useTestSendTemplate(templateKey);

  const onSend = async () => {
    try {
      await mutation.mutateAsync({ subject, html_body, text_body });
    } catch {
      // Error rendered below from mutation.error.
    }
  };

  // Reset success state when the dialog closes.
  React.useEffect(() => {
    if (!open) mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const result = mutation.data;
  const error =
    mutation.error instanceof ApiError ? mutation.error.message : mutation.error?.message;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send a test to your email</DialogTitle>
          <DialogDescription>
            We&apos;ll render this draft with the catalogue&apos;s sample data and deliver a copy to{" "}
            <strong>{callerEmail}</strong>. The recipient cannot be changed — this is intentional
            anti-spam.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 text-sm">
          <div className="bg-muted/30 grid gap-1 rounded-md border px-3 py-2">
            <span className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
              Subject preview
            </span>
            <p className="font-medium">{subject || "—"}</p>
          </div>

          {result && result.success && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <div>
                <p className="font-semibold">Test email queued.</p>
                <p>Check {result.sent_to} in a moment.</p>
              </div>
            </div>
          )}

          {result && !result.success && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <div>
                <p className="font-semibold">Provider didn&apos;t accept the message.</p>
                <p className="opacity-90">{result.error ?? "Unknown error."}</p>
              </div>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Done
          </Button>
          <Button type="button" onClick={onSend} disabled={mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Sending…
              </>
            ) : (
              <>
                <Send className="size-4" aria-hidden="true" />
                Send test now
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
